import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import {
  StyledLimitWrapper,
  StyledLimitSelect,
  IncreaseLimit,
  RealityCheckWrapper,
  StyledCooldownExplanation,
  StyledCooldownExplanationSmall,
  RemainingAmountWrapper,
} from "../styledComponents/StyledLimits";
import {
  StyledButton,
  StyledInput,
  StyledSelect,
} from "../styledComponents/CommonStyledComponents";
import { AccountDescription } from "../styledComponents/AccountPage";
import {
  MUTATION_SET_DEPOSIT_LIMIT_UKGC,
  MUTATION_SET_DEPOSIT_LIMIT_REGIME,
} from "../graphql/mutations";
import {
  QUERY_GET_DEPOSIT_LIMIT_REGIME,
  QUERY_GET_DEPOSIT_LIMIT_REGIME_RESET_TIME,
} from "../graphql/queries";
import { toast } from "react-toastify";
import { RollingLimitRegimeConfirmDialog } from "../modal/RollingLimitRegimeConfirmDialog";
import {
  normalizeDepositPeriodForForm,
  toUkDepositPeriod,
} from "../helpers/depositLimitPeriod";
// import { HTML5_FMT } from "react-moment";

const DepositLimit = ({
  limitsData,
  skinCurrency,
  player,
  depositMaxLimit,
  depositLimits,
  hasLimits,
  handleSubmit,
  handleChange,
  resetLimits,
  isUk,
  realityCheck,
  skin,
  refetchLimits,
  setSpinner,
}) => {
  const { t, i18n } = useTranslation("");
  const { country, limitsLocked } = player;
  const apolloClient = useApolloClient();
  //console.log("country: " + country);
  const localizedFormat = `${(i18n.language || window.localStorage.i18nextLng || "en").split("-")[0]}-${(country || "gb").toUpperCase()}`;

  console.log(window.localStorage.i18nextLng);

  //console.log(player);

  //const [maxValue, setMaxLimit] = useState(depositMaxLimit);
  const { depositValue, depositDate } = limitsData;
  //console.log(depositDate);

  const [hasIncrease /*setHasIncrease*/] = useState(false);
  const [increaseTime /*setIncreaseTime*/] = useState("");
  const [blockChange, setBlockChange] = useState(false);
  const [pendingRegime, setPendingRegime] = useState(null);
  const [depositLimitRegime, setDepositLimitRegime] = useState(null);
  const [amountEdited, setAmountEdited] = useState(false);
  const [regimeResetDate, setRegimeResetDate] = useState("");

  const activeDeposit =
    depositLimits && depositLimits.length > 0 ? depositLimits[0] : null;
  const currentRegime = depositLimitRegime && depositLimitRegime.limitRegime;
  // canBeChanged may arrive as bool or 0/1 — treat 0/false/"0" as locked
  const canChangeRegime = Boolean(
    depositLimitRegime && Number(depositLimitRegime.canBeChanged),
  );
  const targetRegime =
    currentRegime === "nett"
      ? "gross"
      : currentRegime === "gross"
        ? "nett"
        : null;

  const resolvedDepositValue = amountEdited
    ? (depositValue ?? "")
    : depositValue !== "" && depositValue != null
      ? depositValue
      : activeDeposit && activeDeposit.initialAmount != null
        ? activeDeposit.initialAmount / 100
        : "";

  const resolvedDepositDate = normalizeDepositPeriodForForm(
    depositDate || (activeDeposit && activeDeposit.period),
    isUk,
  );

  const getDepositLimitType = () => {
    return toUkDepositPeriod(resolvedDepositDate) + "_deposit";
  };

  const detailsLimitType =
    currentRegime === "nett"
      ? t("label_net_deposit_limit")
      : t("label_deposit_limit");
  const detailsRemainingAmount =
    activeDeposit && activeDeposit.remainingAmount != null
      ? `${skinCurrency}${activeDeposit.remainingAmount / 100}`
      : "";
  const detailsNextUpdate =
    activeDeposit && activeDeposit.resetAt
      ? new Date(activeDeposit.resetAt).toLocaleString("en-GB")
      : "";
  const refetchDepositLimitRegime = async () => {
    const { data, errors } = await apolloClient.query({
      query: QUERY_GET_DEPOSIT_LIMIT_REGIME,
      fetchPolicy: "network-only",
    });
    if (!errors && data?.getDepositLimitRegime) {
      setDepositLimitRegime(data.getDepositLimitRegime);
    }
  };

  const fetchDepositLimitRegimeResetTime = async () => {
    try {
      const { data, errors } = await apolloClient.query({
        query: QUERY_GET_DEPOSIT_LIMIT_REGIME_RESET_TIME,
        fetchPolicy: "network-only",
      });
      if (errors && errors.length) {
        setRegimeResetDate("");
        return;
      }
      const resetTime = data?.getDepositLimitRegimeResetTime?.resetTime;
      if (!resetTime) {
        setRegimeResetDate("");
        return;
      }
      const parsed = new Date(resetTime);
      setRegimeResetDate(
        Number.isNaN(parsed.getTime())
          ? String(resetTime)
          : parsed.toLocaleString("en-GB"),
      );
    } catch (error) {
      console.log("error fetching deposit limit regime reset time", error);
      setRegimeResetDate("");
    }
  };

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
      });
    };
    scrollToTop();
  }, []);

  useEffect(() => {
    if (!isUk) return;
    refetchDepositLimitRegime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, isUk]);

  useEffect(() => {
    if (!pendingRegime) {
      setRegimeResetDate("");
      return;
    }
    fetchDepositLimitRegimeResetTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRegime]);

  useEffect(() => {
    setAmountEdited(false);
  }, [activeDeposit?.initialAmount, activeDeposit?.period]);

  useEffect(() => {
    function hasLimit() {
      /* if (depositLimits[0].increaseAt !== '') {
        setHasIncrease(true);
        setIncreaseTime(depositLimits[0].increaseAt);
      } */

      const hasCooldown =
        !isUk &&
        depositLimits[0].expiresAt !== "" &&
        depositLimits[0].status === "active";
      const hasResetWindow =
        isUk &&
        depositLimits[0].resetAt !== "" &&
        depositLimits[0].status === "active";

      if (hasCooldown || hasResetWindow) {
        //console.log("expiresAt" + JSON.stringify(depositLimits));
        //setMaxLimit(depositValue);
        setBlockChange(true);
      }
    }
    depositLimits.length > 0 && hasLimit();
  }, [depositLimits]);

  const handleRollingLimitRegime = async (limitRegime) => {
    setSpinner(true);
    try {
      const { data, errors } = await apolloClient.mutate({
        mutation: MUTATION_SET_DEPOSIT_LIMIT_REGIME,
        variables: { limitRegime },
        errorPolicy: "all",
      });
      if (errors && errors.length) {
        toast.error(t(errors[0].message), { position: "bottom-center" });
        return;
      }
      if (data?.setDepositLimitRegime?.ok) {
        toast.success(t("success", "Success"), {
          position: "bottom-center",
        });
        setPendingRegime(null);
        await refetchDepositLimitRegime();
        if (refetchLimits) {
          await refetchLimits();
        }
      }
    } catch (error) {
      console.log("error setting deposit limit regime", error);
      toast.error(t("error_generic", "Something went wrong"), {
        position: "bottom-center",
      });
    } finally {
      setPendingRegime(null);
      setSpinner(false);
    }
  };

  const handleDepositLimitChange = async () => {
    // UK deposit limits always use CreateDepositLimit (1day/7days/30days)
    if (isUk) {
      setSpinner(true);
      try {
        const amount =
          parseFloat(
            resolvedDepositValue !== "" ? resolvedDepositValue : depositValue,
          ) * 100;
        const { data, errors } = await apolloClient.mutate({
          mutation: MUTATION_SET_DEPOSIT_LIMIT_UKGC,
          variables: {
            limitType: getDepositLimitType(),
            initialAmount: amount,
          },
          errorPolicy: "all",
        });
        if (errors && errors.length) {
          toast.error(t(errors[0].message), { position: "bottom-center" });
          return;
        }
        if (data) {
          toast.success(t("suc_limit_message"), {
            position: "bottom-center",
          });
          if (refetchLimits) {
            await refetchLimits();
          }
        }
      } catch (error) {
        toast.error(error.message || "Error", { position: "bottom-center" });
      } finally {
        setSpinner(false);
      }
    } else {
      // non-UK: CreateLimits — spinner handled inside handleSubmit/limitAccount
      handleSubmit("deposit");
    }
  };

  return (
    <>
      {(skin === "fun" || skin === "hyc" || skin === "cac") && (
        <AccountDescription
          expand={true}
          description
          dangerouslySetInnerHTML={{
            __html: t("lable_desposit_limit_description"),
          }}
        >
          {/* <p>
            Daily - a period of a single day*
            <br />
            Weekly - a period of seven days, from Monday to Sunday*
            <br />
            Monthly - a calendar month, from the first day of the month
            <br />
            till the last day of the month*
            <br />
            <br />
            *) From 00:00 till 23:59 UTC
            <br />
            <br />
            -Any increase in limits will go through a 24 hour cooling period
            before being applied.
            <br />
            <br />
          </p>
          <p>
            -Note that a weekly time limit can't be less than a daily time limit
            and monthly time limit can't be less than a weekly time limit.
            <br />
            <br />
            Players residing in the UK have the following default limits
            applied:
            <br />
            Deposit: 100.000
            <br />
            Bet: 100.000.000
            <br />
            Loss: 100.000.000
            <br />
            <br />
            Player residing in Sweden are not allowed to play without a deposit
            limit.
            <br />
            <br />
          </p> */}
        </AccountDescription>
      )}
      <StyledLimitWrapper>
        <StyledLimitSelect>
          <label>{t("label_choose_limit", "Period")}</label>
          <StyledSelect
            data-cy="deposit-limit-period"
            onChange={(e) => handleChange(e)}
            value={resolvedDepositDate}
            name="depositDate"
            disabled={
              (isUk &&
                player.ragAffordability &&
                player.ragAffordability === "Red") ||
              (isUk && depositLimitRegime != null && !canChangeRegime)
            }
          >
            <option value={isUk ? "1day" : "daily"}>
              {t("label_daily", "Daily*")}
            </option>
            <option value={isUk ? "7days" : "weekly"}>
              {t("label_weekly", "Weekly*")}
            </option>
            <option value={isUk ? "30days" : "monthly"}>
              {t("label_monthly", "Monthly*")}
            </option>
          </StyledSelect>
        </StyledLimitSelect>
        <StyledLimitSelect>
          <label>
            {t("label_amount", "Amount")} ({skinCurrency})
          </label>
          <div className="input-group">
            <StyledInput
              data-cy="deposit-limit-amount"
              limit
              type="number"
              min="1"
              max={depositMaxLimit}
              step="1"
              value={resolvedDepositValue}
              name="depositValue"
              onChange={(e) => {
                setAmountEdited(true);
                handleChange(e);
              }}
              disabled={
                hasIncrease ||
                (isUk && limitsLocked) ||
                (isUk &&
                  player.ragAffordability &&
                  player.ragAffordability === "Red") ||
                (isUk && depositLimitRegime != null && !canChangeRegime)
              }
            />
            <div className="input-group-append">
              <span className="input-group-text">{skinCurrency}</span>
            </div>
          </div>
        </StyledLimitSelect>
        {hasLimits && isUk ? (
          <StyledButton
            limit
            onClick={() => resetLimits()}
            disabled={isUk && limitsLocked}
          >
            {t("label_reset", "Reset")}
          </StyledButton>
        ) : (
          <StyledButton
            limit
            pub={skin === "pub"}
            disabled={
              hasIncrease ||
              (isUk && limitsLocked) ||
              (isUk &&
                player.ragAffordability &&
                player.ragAffordability === "Red") ||
              (isUk && depositLimitRegime != null && !canChangeRegime)
            }
            data-cy="deposit-limit-save"
            onClick={() => handleDepositLimitChange()}
          >
            {t("label_save", "Save")}
          </StyledButton>
        )}
        {blockChange && (
          <StyledCooldownExplanation cooldownprop depositlimit>
            <StyledCooldownExplanationSmall>
              {isUk ? (
                <>
                  {t("label_next_update")}{" "}
                  {new Date(depositLimits[0].resetAt).toLocaleString("en-GB")} |{" "}
                  {t("label_reset_amount")} {skinCurrency}
                  {Math.max(
                    0,
                    (depositLimits[0].resetAmount || 0) -
                      (depositLimits[0].remainingAmount || 0),
                  ) / 100}
                </>
              ) : (
                <>
                  {t("cooldown_explanation")}{" "}
                  {new Date(depositLimits[0].expiresAt).toLocaleString(
                    localizedFormat,
                  )}
                </>
              )}
            </StyledCooldownExplanationSmall>
          </StyledCooldownExplanation>
        )}
      </StyledLimitWrapper>

      {depositLimits[0] && (
        <RemainingAmountWrapper>
          {t("label_remaining_limit_amount") +
            ": " +
            skinCurrency +
            depositLimits[0].remainingAmount / 100}
        </RemainingAmountWrapper>
      )}
      {isUk && depositLimits[0] && depositLimits[0].expiresAt !== "" && (
        <RealityCheckWrapper resetAt={true}>
          {t("cooldown_explanation_expires_at")}{" "}
          {new Date(depositLimits[0].expiresAt).toLocaleString(localizedFormat)}
        </RealityCheckWrapper>
      )}
      {skin === "abc" && (
        <RealityCheckWrapper>
          {realityCheck !== -1 && (
            <p>
              {t("label_reality_check")} {Math.floor(realityCheck / 60)}{" "}
              {t("label_reality_check_minutes")}
            </p>
          )}
        </RealityCheckWrapper>
      )}
      {isUk ? (
        <AccountDescription
          expand={true}
          description
          dangerouslySetInnerHTML={{
            __html: t("label_message_deposit_limit_details", {
              currency: skinCurrency || "",
              limitType: detailsLimitType,
              remainingAmount: detailsRemainingAmount,
              nextUpdate: detailsNextUpdate,
              newRemainingAfterUpdate: activeDeposit
                ? `${skinCurrency}${(activeDeposit.resetAmount || 0) / 100}`
                : "",
            }),
          }}
        />
      ) : (
        <AccountDescription
          expand={true}
          description
          dangerouslySetInnerHTML={{ __html: t("message_limit_details") }}
        ></AccountDescription>
      )}
      {isUk && targetRegime && (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            padding: "1.5rem 0",
          }}
        >
          <p style={{ marginBottom: "0.75rem" }}>
            {t("label_rolling_limit_choice")}
          </p>
          <StyledButton
            limit
            pub={skin === "pub"}
            disabled={
              limitsLocked ||
              !canChangeRegime ||
              (activeDeposit &&
                activeDeposit.expiresAt !== "" &&
                activeDeposit.status === "active")
            }
            onClick={() => setPendingRegime(targetRegime)}
            style={{ margin: "0.5rem" }}
          >
            {targetRegime === "gross"
              ? t("label_want_gross_limit")
              : t("label_want_nett_limit")}
          </StyledButton>
        </div>
      )}
      <RollingLimitRegimeConfirmDialog
        showModal={Boolean(pendingRegime)}
        targetRegime={pendingRegime}
        skin={skin}
        resetDate={regimeResetDate}
        handleProceed={() => handleRollingLimitRegime(pendingRegime)}
        handleCancel={() => setPendingRegime(null)}
      />
    </>
  );
};

export default DepositLimit;
